<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="error.message" />

        <v-alert
          v-if="bankAccountMayBeInvalid"
          :value="!!bankAccountMayBeInvalid"
          color="warning"
          icon="warning"
          transition="scale-transition"
        >
          {{ $t('@repaymentForm/bankAccountMayBeInvalid') }}
        </v-alert>

        <v-form ref="form" @submit.prevent="onSubmit" color="green">
          <v-card-title class="headline">{{ $t('@repaymentForm/create') }}</v-card-title>
          <v-divider></v-divider>

          <v-card-text style="max-height: 700px; overflow: auto">
            <v-flex xs12>
              <v-select
                :items="invoiceItems"
                v-model="form.invoices"
                :disabled="submitting"
                :label="$t('@repaymentForm/invoices')"
                single-line
                :rules="[validateRequired]"
                :error-messages="validationError(error.errorField, errorFieldMap.clearingNumber)"
                class="my-0 mb-2 px-1"
                persistent-hint
                multiple
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0">
                    {{ form.invoices.length }}
                    {{ $t('@repaymentForm/selected') }} -
                    {{
                      form.invoices.reduce((acc, id) => {
                        const invoice = invoices.find((inv) => inv.id === id);
                        return acc + parseInt(invoice.paid);
                      }, 0)
                    }}
                    SEK
                  </span>
                </template>
              </v-select>

              <v-container grid-list-md fluid pa-0 ma-0>
                <v-layout row>
                  <v-flex xs12>
                    <v-select
                      :items="bankDetailsItems"
                      :disabled="submitting"
                      :label="$t('@repaymentForm/bankDetails')"
                      single-line
                      class="my-0 mb-2 px-1"
                      persistent-hint
                      @change="onBankDetailsSelected"
                    ></v-select>
                  </v-flex>
                </v-layout>

                <v-layout row wrap v-if="enteringBankAccountManually">
                  <v-flex col xs6>
                    <v-text-field
                      v-model="form.clearingNumber"
                      color="green darken-1"
                      :label="$t('@repaymentForm/clearingNumberInputLabel')"
                      :rules="[validateRequired]"
                      :error-messages="
                        validationError(error.errorField, errorFieldMap.clearingNumber)
                      "
                      required
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs6>
                    <v-text-field
                      v-model="form.accountNumber"
                      color="green darken-1"
                      :label="$t('@repaymentForm/accountNumberInputLabel')"
                      :rules="[validateRequired]"
                      :error-messages="
                        validationError(error.errorField, errorFieldMap.accountNumber)
                      "
                      required
                    ></v-text-field>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-flex>

            <h3 class="subheading mb-2">{{ $t('@repaymentForm/history') }}</h3>
            <v-data-table
              :headers="headers"
              :items="repayments"
              :loading="loading"
              :total-items="pagination.totalItems"
              :pagination.sync="pagination"
              :rows-per-page-items="rowsPerPageItems"
              item-key="_id"
              :hide-actions="hideActions"
            >
              <template v-slot:items="props">
                <td>{{ props.item.id }}</td>
                <td>{{ props.item.createdAt | defaultDateFormatter }}</td>
                <td>{{ props.item.status }}</td>
                <td>{{ props.item.sum }}</td>
                <td>{{ props.item.numInvoices }}</td>
                <td>{{ props.item.clearingNumber }}, {{ props.item.accountNumber }}</td>
                <td>{{ createdBy(props.item.byEmployee) }}</td>
              </template>
            </v-data-table>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-btn color="red darken-1" flat @click.stop="closeModal">
              {{ $t('@repaymentForm/cancelButtonLabel') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn primary flat :disabled="isDisabled" type="submit">
              {{ $t('@repaymentForm/createButtonLabel') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import Kontonummer from 'kontonummer';
import { mapState } from 'vuex';
import ErrorsList from '../../components/ErrorsList';
import { validationError } from '../../errorHandlers';
import { defaultDateFormatter, defaultDateTimeFormatter, monthDateFormatter } from '../../utils';

const errorFieldMap = {
  clearingNumber: 'clearingNumber',
  accountNumber: 'accountNumber',
  invoices: 'invoices',
};

export default {
  components: { ErrorsList },
  props: {
    contract: {
      type: Object,
    },
  },

  data() {
    return {
      submitting: false,
      enteringBankAccountManually: false,
      bankAccountMayBeInvalid: false,
      form: {
        invoices: [],
        clearingNumber: '',
        accountNumber: '',
      },
      invoices: [],
      bankDetails: [],
      repayments: [],
      error: {
        message: '',
        field: '',
      },
      statuses: {
        pending: this.$t('@repaymentStatus/pending'),
        repaid: this.$t('@repaymentStatus/repaid'),
        completed: this.$t('@repaymentStatus/completed'),
      },
      headers: [
        {
          text: this.$t('@repaymentForm/id'),
          align: 'left',
          sortable: false,
          value: 'id',
        },
        {
          text: this.$t('@repaymentForm/created'),
          align: 'left',
          sortable: true,
          value: 'createdAt',
        },
        {
          text: this.$t('@repaymentForm/status'),
          align: 'left',
          value: 'status',
          sortable: false,
        },
        {
          text: this.$t('@repaymentForm/sum'),
          align: 'left',
          value: 'sum',
          sortable: false,
        },
        {
          text: this.$t('@repaymentForm/numInvoices'),
          align: 'left',
          value: 'numInvoices',
          sortable: false,
        },
        {
          text: this.$t('@repaymentForm/accountNumber'),
          align: 'left',
          value: 'accountNumber',
          sortable: false,
        },
        { text: this.$t('@repaymentForm/createdBy'), value: 'byEmployee', sortable: false },
      ],
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 5,
        totalItems: 0,
      },
      loading: false,
      rowsPerPageItems: [5, 10, 15],
      lastQueryString: '',
      validationError,
      errorFieldMap,
    };
  },

  mounted() {
    this.fetch();
  },

  methods: {
    fetch() {
      this.fetchInvoices();
      this.fetchBankDetails();
      this.fetchRepayments();
      this.fetchRepaymentPermissions();
    },

    closeModal() {
      this.reset();
      this.$emit('dismiss');
    },

    async onSubmit() {
      this.error = { message: '', field: '' };
      this.submitting = true;

      try {
        await this.$http.post(`/api/repayments`, {
          insuranceId: this.contract.id,
          invoiceIds: this.form.invoices,
          clearingNumber: this.form.clearingNumber,
          accountNumber: this.form.accountNumber,
        });

        this.form.invoices.splice(0);
        this.fetch();
      } catch (e) {
        if (e.response && e.response.data) {
          this.error = {
            message: e.response.data.message,
            field: e.response.data.field,
          };
        } else {
          this.error = {
            message: this.$t('@repaymentForm/unknownError'),
            field: '',
          };
        }
      } finally {
        this.submitting = false;
      }
    },

    reset() {
      this.error = { message: '', field: '' };
      this.form.clearingNumber = '';
      this.form.accountNumber = '';
      this.form.invoices.splice(0);
      this.loading = false;
    },

    validateRequired(val) {
      const isFilled = val !== undefined && val !== null && val !== '';
      return isFilled || this.$t('@newContractForm/emptyRequiredFieldErrorMessage');
    },

    createdBy(user) {
      return user
        ? `${user.firstName} ${user.lastName}`
        : this.$t('@auditTrailEventsList/systemUserName');
    },

    async fetchInvoices() {
      const { data } = await this.$http.get(`/api/insurance/${this.contract.id}/invoices`);

      this.invoices = data.items;
    },

    async fetchBankDetails() {
      const { data } = await this.$http.get(`/api/insurance/${this.contract.id}/bank-details`);

      this.bankDetails = data.bankDetails;
    },

    async fetchRepayments() {
      const { data } = await this.$http.get(`/api/insurance/${this.contract.id}/repayments`);

      const repaymentsWithStatuses = data.items.map((item) => ({
        ...item,
        status: this.statuses[item.status],
      }));

      this.repayments = repaymentsWithStatuses;
    },

    async fetchRepaymentPermissions() {
      const { data } = await this.$http.get(
        `/api/insurance/${this.contract.id}/get-repayment-permissions`,
      );

      if (data.allowed === false) {
        this.error = {
          message: `${this.$t('@repaymentForm/repaymentNotAllowed')} ${data.reason}`,
          field: '',
        };
      }
    },

    onBankDetailsSelected(value) {
      if (value === null) {
        this.enteringBankAccountManually = true;
        return;
      }

      this.enteringBankAccountManually = false;
      const item = this.bankDetails.find((item) => item.id === value);

      this.form.accountNumber = item?.bankNumber || '';
      this.form.clearingNumber = item?.clearingNumber || '';
    },

    checkAccountNumber() {
      if (this.form.clearingNumber && this.form.accountNumber) {
        try {
          const account = new Kontonummer(this.form.clearingNumber, this.form.accountNumber, {
            mode: 'lax',
          });

          if (account.valid) {
            this.bankAccountMayBeInvalid = false;
            return;
          }

          this.bankAccountMayBeInvalid = true;
        } catch (e) {
          this.bankAccountMayBeInvalid = true;
        }

        return;
      }

      this.bankAccountMayBeInvalid = false;
    },
  },

  computed: {
    ...mapState({
      currentUser: (state) => state.auth.currentUser,
    }),

    currentUserIsAdmin() {
      return this.currentUser?.roles.includes('insurance.admin');
    },

    hideActions() {
      return !this.pagination.totalItems || this.pagination.totalItems < 5;
    },

    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const query = {
        skip,
        take,
      };

      return query;
    },

    numSelectedInvoices() {
      return this.form.invoices.length;
    },

    selectedInvoicesSum() {
      return this.form.invoices.reduce((acc, id) => {
        const invoice = this.invoices.find((inv) => inv.id === id);

        acc += parseFloat(invoice?.paid || 0);

        return acc;
      }, 0);
    },

    invoiceItems() {
      return this.invoices.map((invoice) => ({
        text: `${monthDateFormatter(invoice.periodStart)} - ${monthDateFormatter(
          invoice.periodEnd,
        )} - Betalt: ${invoice.paid}${invoice.paid != invoice.sum ? ' av ' + invoice.sum : ''} SEK`,
        value: invoice.id,
        disabled:
          invoice.repaid || (invoice.status !== 'processed' && invoice.status !== 'approved'),
      }));
    },

    bankDetailsItems() {
      const items = this.bankDetails.map((bankDetail) => ({
        text: `${bankDetail.clearingNumber} - ${bankDetail.bankNumber}`,
        value: bankDetail.id,
      }));

      items.push({ value: null, text: `- ${this.$t('@repaymentForm/enterManually')} -` });

      return items;
    },

    isDisabled() {
      return (
        !this.form.clearingNumber ||
        !this.form.accountNumber ||
        this.form.invoices.length == 0 ||
        (!this.currentUserIsAdmin && this.form.invoices.length > 3) ||
        this.submitting
      );
    },
  },

  filters: {
    defaultDateTimeFormatter,
    defaultDateFormatter,
  },

  watch: {
    pagination() {
      //this.fetch();
    },

    'form.accountNumber'() {
      void this.checkAccountNumber();
    },

    'form.clearingNumber'() {
      void this.checkAccountNumber();
    },

    'form.invoices': {
      handler() {
        this.error = { message: '', field: '' };
        if (!this.currentUserIsAdmin && this.form.invoices.length > 3) {
          this.error = {
            message: this.$t('@repaymentForm/tooManyInvoicesSelectedError'),
            field: 'invoices',
          };
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
>>> .v-datatable__actions__pagination {
  display: none;
}
table.v-table tbody td.column-title {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.54) !important;
}
</style>
